import { yupResolver } from '@hookform/resolvers/yup';
import { WebsiteDetail } from '@thurstech/commons/dist/entity/prop';
import { FIELD_DETAILS, FieldName } from '@thurstech/commons/dist/entity/user';
import { PresignedPostUploadType } from '@thurstech/commons/dist/models/common';
import { ImageToString } from '@thurstech/commons/dist/utils/types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Categories, { CategoriesProps } from './Categories';
import { InstagramLogin } from './InstagramLogin';
import VideoUploadModal from './VideoUploadModal';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import { InputColor } from '../../components/input/InputColor';
import InputFile, { uploadLocalFiles } from '../../components/input/InputFile';
import InputSlider from '../../components/input/InputSlider';
import InputTextArea from '../../components/input/InputTextArea';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import {
  getBusinessDetailThunk,
  getWebsiteDetailThunk,
  saveWebsiteDetailThunk
} from '../../redux/reducer/propSlice';
import { FileType, withErrorHandler } from '../../utils/utils';
import { WebsiteDetailFormData, websiteDetailSchemma } from '../../validation/general/websiteDetails';
import FAQSection from '../FaqsComponent';

const WebsiteDetails: React.FC = () => {
  const { businessDetail, websiteDetail, isLoading } = useAppSelector(state => state.prop);
  let values: WebsiteDetailFormData | undefined = undefined;

  if (websiteDetail) {
    const videoSection = websiteDetail.videoSection;
    values = {
      ...websiteDetail,
      isCancellationAllowed: !!websiteDetail?.cancellation,
      videoSection: videoSection
        ? {
            ...videoSection,
            videos: videoSection.videos.map(({ src, thumbnail }) => ({ src: { loc: src }, thumbnail }))
          }
        : undefined,
      dataToCollect: Object.entries(websiteDetail.dataToCollect ?? {}).reduce(
        (prev, [key, value]) => {
          prev[key as FieldName] = { collect: true, required: value.required };
          return prev;
        },
        {} as Partial<Record<FieldName, { collect: boolean; required: boolean }>>
      )
    } as WebsiteDetailFormData;
  }
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    setValue,
    watch,
    trigger
  } = useForm<WebsiteDetailFormData>({
    resolver: yupResolver(websiteDetailSchemma),
    defaultValues: { faqs: [{}], secondaryColor: '#000000' },
    values
  });
  const dispatch = useAppDispatch();
  const faqs = watch('faqs');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getWebsiteDetailThunk());
    if (!businessDetail) {
      dispatch(getBusinessDetailThunk());
    }
  }, [dispatch, businessDetail]);

  const onSubmit = (data: WebsiteDetailFormData) =>
    withErrorHandler(async () => {
      const logo = (await uploadLocalFiles(PresignedPostUploadType.PROP_LOGO, data.logo))[0];
      const landingImages = await uploadLocalFiles(
        PresignedPostUploadType.PROP_LANDING_IMAGES,
        ...data.landingImages
      );
      setValue(
        'landingImages',
        landingImages.map(loc => ({ loc }))
      );
      const categoriesImageLoc = await uploadLocalFiles(
        PresignedPostUploadType.PROP_CATEGORY_IMAGES,
        ...data.categories.map(category => category.image)
      );
      setValue(
        'categories',
        data.categories.map((obj, index) => ({ ...obj, image: { loc: categoriesImageLoc[index] } }))
      );
      const uploadedAboutImages = await uploadLocalFiles(
        PresignedPostUploadType.PROP_ABOUT_IMAGES,
        ...data.about.images
      );
      setValue(
        'about.images',
        uploadedAboutImages.map(loc => ({ loc }))
      );

      let videoSection = undefined;
      if (data.videoSection?.videos) {
        const videoFiles = data.videoSection.videos.map(videoItem => videoItem.src) as FileType[];
        const uploadedVideoUrls = await uploadLocalFiles(PresignedPostUploadType.PROP_VIDEO, ...videoFiles);

        // Get only the thumbnail files that are not null
        const thumbnailFiles = data.videoSection.videos
          .map(videoItem => videoItem.thumbnail)
          .filter((image): image is FileType => image !== null);

        const uploadThumbnailUrls = await uploadLocalFiles(
          PresignedPostUploadType.PROP_THUMBNAIL,
          ...thumbnailFiles
        );

        // Create a mapping of original video indices to uploaded thumbnail URLs
        let thumbnailUploadIndex = 0;
        const thumbnailArray = data.videoSection.videos.map(videoItem => {
          if (videoItem.thumbnail) {
            // If this video has a thumbnail, use the next uploaded thumbnail URL
            return uploadThumbnailUrls[thumbnailUploadIndex++];
          }
          return null;
        });
        videoSection = {
          title: data.videoSection?.title || '',
          description: data.videoSection?.description || '',
          videos: uploadedVideoUrls.map((url, index) => ({
            src: url,
            thumbnail: thumbnailArray[index]
          }))
        };
      }

      const payload = {
        ..._.omit(data, 'isCancellationAllowed', 'isInstagramConnected', 'videoSection'),
        logo,
        landingImages,
        about: {
          title: data.about.title,
          description: data.about.description,
          images: uploadedAboutImages
        },
        faqs: faqs.map(faq => ({ question: faq.question, answer: faq.answer })),
        categories: data.categories.map((obj, index) => ({ ...obj, image: categoriesImageLoc[index] })),
        videoSection,
        dataToCollect: Object.entries(data.dataToCollect)
          .filter(([_key, value]) => value.collect)
          .reduce(
            (prev, [key, value]) => {
              prev[key as FieldName] = { required: value.required };
              return prev;
            },
            {} as Partial<Record<FieldName, { required: boolean }>>
          )
      } as ImageToString<WebsiteDetail>;

      await dispatch(saveWebsiteDetailThunk(payload)).unwrap();
    }, 'Successfully saved Website Details');

  const commonProps = {
    topLabel: true,
    required: true,
    register,
    errors
  };
  const [displayDomain, setDisplayDomain] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (websiteDetail?.domain) {
      fetch(websiteDetail.domain, { mode: 'no-cors' })
        .then(r => setDisplayDomain(websiteDetail.domain))
        .catch(e => setDisplayDomain(businessDetail?.generatedDomain));
    } else {
      setDisplayDomain(businessDetail?.generatedDomain);
    }
  }, [websiteDetail?.domain, businessDetail?.generatedDomain]);

  return (
    <main className="p-8 w-fulll min-h-screen">
      <form onSubmit={handleSubmit(onSubmit)} className="flex items-start">
        <div className="w-[85%] pr-20">
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Website Theme</h3>
            {(websiteDetail || isSubmitSuccessful) && displayDomain && (
              <div className="mb-4 text-lg">
                Your website would be viewable at{' '}
                <a
                  href={`https://${displayDomain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-500"
                >
                  {displayDomain}
                  <span className="fas fa-external-link-alt"></span>
                </a>
              </div>
            )}
            <InstagramLogin />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <InputColor
                  label="Primary Color"
                  name="primaryColor"
                  type="text"
                  placeholder="Enter Primary color hex code"
                  watch={watch}
                  setValue={setValue}
                  {...commonProps}
                />
                <InputColor
                  label="Secondary Color"
                  name="secondaryColor"
                  type="text"
                  placeholder="Enter Secondary color hex code"
                  watch={watch}
                  setValue={setValue}
                  {...commonProps}
                />
                <Input
                  label="Website Domain"
                  name="domain"
                  type="text"
                  placeholder="Enter your website domain"
                  {...commonProps}
                  required={false}
                />
              </div>
              <InputFile
                name="logo"
                label="Upload Logo"
                required
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                addtionalText={
                  <p className="text-sm font-medium mt-1 text-gray-300">Click here to add logo</p>
                }
                accept="image/*"
              />
            </div>
          </section>

          {/* Website Details Section */}
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Website Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input label="Title" name="title" type="text" placeholder="Enter Title" {...commonProps} />
                <InputTextArea
                  label="Description"
                  name="description"
                  maxLength={500}
                  placeholder="Enter Description"
                  {...commonProps}
                />
                <Categories
                  {...({ register, errors, setValue, watch, trigger } as unknown as CategoriesProps)}
                />
                <Input
                  label="Categories Title"
                  name="categoriesTitle"
                  type="text"
                  placeholder="Enter Categories Title"
                  {...commonProps}
                />
              </div>
              <InputFile
                name="landingImages"
                label="Landing Images"
                required
                multiple
                maxFiles={5}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                addtionalText={
                  <p className="text-sm font-medium mt-1 text-gray-300">Click here to add images</p>
                }
                accept="image/*"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Statistics Title"
                name="aggregatedStatTitle"
                type="text"
                placeholder="Enter Aggregated Statistics Title"
                {...commonProps}
              />
              <Input
                label="Statistics Description"
                name="aggregatedStat"
                type="text"
                placeholder="Enter Aggregated Statistics"
                {...commonProps}
              />
              <Input
                label="Rating Title"
                name="ratingTitle"
                type="text"
                placeholder="Enter Rating Title"
                {...commonProps}
              />
              <Input
                label="Rating Description"
                name="rating"
                type="text"
                placeholder="Enter Rating"
                {...commonProps}
              />
              <Input
                label="Product Title"
                name="productTitle"
                type="text"
                placeholder="Enter Product Title"
                {...commonProps}
              />
            </div>
          </section>
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">About</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Title"
                  name="about.title"
                  type="text"
                  placeholder="Enter Title"
                  {...commonProps}
                />
                <InputTextArea
                  label="Description"
                  name="about.description"
                  maxLength={500}
                  placeholder="Enter Description"
                  {...commonProps}
                />
              </div>
              <InputFile
                name="about.images"
                label="Images"
                required
                multiple
                maxFiles={4}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                addtionalText={
                  <p className="text-sm font-medium mt-1 text-gray-300">Click here to add images</p>
                }
                accept="image/*"
              />
            </div>
          </section>
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Data to Collect from user</h3>
            <div className="text-lg font-bold grid grid-cols-3 mb-2">
              <span>Field Name</span>
              <span>Collect Field?</span>
              <span>Is Required?</span>
            </div>
            {Object.values(FieldName).map(name => (
              <div key={name} className="grid grid-cols-3 gap-8 ch">
                <label className="font-semibold">{FIELD_DETAILS[name].displayName}</label>
                <InputSlider register={register} errors={errors} name={`dataToCollect.${name}.collect`} />
                <InputSlider
                  register={register}
                  errors={errors}
                  name={`dataToCollect.${name}.required`}
                  disabled={!watch(`dataToCollect.${name}.collect`)}
                />
              </div>
            ))}
          </section>
          {/* FAQs Section */}
          <FAQSection
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            trigger={trigger}
            name="faqs"
          />

          {/* Policies Section */}
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Policies</h3>
            <div className="grid grid-cols-2 gap-4">
              <InputTextArea
                label="Terms & Conditions"
                name="termsAndCondition"
                maxLength={500}
                placeholder="Enter Terms & Conditions"
                {...commonProps}
              />
              <InputTextArea
                label="Cancellation Policies"
                name="cancellationPolicy"
                maxLength={500}
                placeholder="Enter Cancellation Policies"
                {...commonProps}
              />
              <InputTextArea
                label="Privacy Policies"
                name="privacyPolicy"
                maxLength={500}
                placeholder="Enter Privacy Policies"
                {...commonProps}
              />
              <div className="mt-2">
                <InputSlider
                  label="Cancellation Allowed"
                  name="isCancellationAllowed"
                  {...commonProps}
                  required={false}
                />
                {watch('isCancellationAllowed') && (
                  <Input
                    label="Minimum Time (In Hours)"
                    name={`cancellation.minTime`}
                    type="number"
                    placeholder="Enter Minimum Time (In Hours)"
                    {...commonProps}
                  />
                )}
              </div>
            </div>
          </section>
          {/* Links Section */}
          <section className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Social Links</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Facebook"
                name="links.facebook"
                type="text"
                placeholder="Enter Facebook Link"
                {...commonProps}
                required={false}
              />
              <Input
                label="Instagram"
                name="links.instagram"
                type="text"
                placeholder="Enter Instagram Link"
                {...commonProps}
                required={false}
              />
              <Input
                label="Youtube"
                name="links.youtube"
                type="text"
                placeholder="Enter Youtube Link"
                {...commonProps}
                required={false}
              />
            </div>
          </section>
          {process.env.REACT_APP_STAGE !== 'prod' && (
            <section className="mb-8">
              <h3 className="text-2xl font-semibold mb-4">Videos</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Video Section Title"
                    name="videoSection.title"
                    type="text"
                    placeholder="Enter Title"
                    {...commonProps}
                  />
                  <InputTextArea
                    label="Video Section Description"
                    name="videoSection.description"
                    maxLength={500}
                    placeholder="Enter Description"
                    {...commonProps}
                  />
                </div>
                <div>
                  <label
                    htmlFor="video-upload-button"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Videos *
                  </label>
                  <button
                    id="video-upload-button"
                    type="button"
                    onClick={() => setIsVideoModalOpen(true)}
                    className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 transition-colors"
                  >
                    <div className="text-center">
                      <svg
                        className="mx-auto h-8 w-8 text-gray-400 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm font-medium text-gray-700">Upload Videos</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {watch('videoSection.videos')?.length || 0} video(s) selected
                      </p>
                    </div>
                  </button>
                  {errors.videoSection?.videos && (
                    <p className="text-red-600 text-sm font-semibold mt-1">
                      {errors.videoSection.videos.message}
                    </p>
                  )}
                </div>
              </div>
              <VideoUploadModal
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                setValue={setValue}
                watch={watch}
                trigger={trigger}
                errors={errors}
                maxFiles={5}
              />
            </section>
          )}
        </div>
        <Button
          type="submit"
          className="mt-12 sticky top-14 p-2 px-6 bg-[#35e0a1] text-black rounded-md font-semibold hover:bg-teal-600 transition duration-300"
          label="Save Changes"
          isLoading={isLoading[saveWebsiteDetailThunk.pending.toString()] || isSubmitting}
        />
      </form>
    </main>
  );
};

export default WebsiteDetails;
